import { Ban, CircleCheck, CircleX, Copy, FileSignature, Printer, Send } from "lucide-react";
import { Link } from "@/lib/nav";
import { useState } from "react";
import { toast } from "sonner";

import Field from "@/components/field";
import { Button } from "@buildpy/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@buildpy/ui/dialog";
import { Input } from "@buildpy/ui/input";
import { Textarea } from "@buildpy/ui/textarea";
import {
  acceptChangeOrderByStaff,
  rejectChangeOrder,
  sendChangeOrder,
  voidChangeOrder,
} from "@/api/actions/change-order";
import { canTransition } from "@buildpy/core/modules/change-order/transitions";
import type { ChangeOrderStatus } from "@buildpy/core/modules/change-order/types";
import { useRecordAction } from "@/modules/project/ui/use-record-action";

interface Props {
  contractId: string;
  changeOrderId: string;
  status: ChangeOrderStatus;
  // 보내기·승인을 막는 이유(없으면 null) — 서버가 전이표로 정해 넘긴다.
  sendBlock: string | null;
  acceptBlock: string | null;
  // 살아 있는 계약 고객 링크(절대 URL). 없으면 복사 버튼을 숨긴다.
  shareUrl: string | null;
}

type Open = "send" | "accept" | "reject" | "void" | null;

// 변경공사 액션 바 — 어떤 버튼이 보이는지는 전이표, 눌러도 되는지는 서버가 넘긴 막는 이유가 정한다.
const ChangeOrderActions = ({ contractId, changeOrderId, status, sendBlock, acceptBlock, shareUrl }: Props) => {
  const { pending, run } = useRecordAction();
  const [open, setOpen] = useState<Open>(null);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");

  const close = () => setOpen(null);
  const paper = status === "DRAFT";

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("링크를 복사했습니다 — 고객에게 다시 보내 주세요");
    } catch {
      toast.error("복사하지 못했습니다 — 링크를 직접 선택해 복사해 주세요");
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="change-order-actions">
      {canTransition(status, "SENT") && (
        <Button disabled={pending || sendBlock !== null} size="sm" title={sendBlock ?? undefined} variant="primary" onClick={() => setOpen("send")}>
          <Send />
          보내기
        </Button>
      )}
      {status === "SENT" && shareUrl && (
        <Button disabled={pending} size="sm" variant="outline" onClick={() => void copy(shareUrl)}>
          <Copy />
          고객 링크 복사
        </Button>
      )}
      {canTransition(status, "ACCEPTED") && (
        <Button
          disabled={pending || acceptBlock !== null}
          size="sm"
          title={acceptBlock ?? undefined}
          variant="outline"
          onClick={() => setOpen("accept")}>
          {paper ? <FileSignature /> : <CircleCheck />}
          {paper ? "종이로 승인 받음" : "승인 기록"}
        </Button>
      )}
      {canTransition(status, "REJECTED") && (
        <Button disabled={pending} size="sm" variant="outline" onClick={() => setOpen("reject")}>
          <CircleX />
          거절 기록
        </Button>
      )}
      {canTransition(status, "VOID") && (
        <Button disabled={pending} size="sm" variant="destructive" onClick={() => setOpen("void")}>
          <Ban />
          파기
        </Button>
      )}
      <Button asChild size="sm" variant="outline">
        <Link href={`/app/contracts/${contractId}/changes/${changeOrderId}/print`}>
          <Printer />
          인쇄
        </Link>
      </Button>

      <Dialog open={open === "send"} onOpenChange={(v) => !v && close()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>변경공사 보내기</DialogTitle>
            <DialogDescription>
              고객은 계약서 링크에서 변경 내용을 보고 승인합니다. 보낸 뒤에는 고칠 수 없고, 고치려면 파기한 뒤 새로 만듭니다.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button disabled={pending} size="sm" variant="ghost" onClick={close}>
              취소
            </Button>
            <Button
              disabled={pending}
              size="sm"
              variant="primary"
              onClick={() =>
                void run("보내지 못했습니다", () => sendChangeOrder(changeOrderId), () => {
                  toast.success("보냈습니다 — 계약 링크를 고객에게 다시 보내 주세요");
                  close();
                })
              }>
              보내기
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open === "accept"} onOpenChange={(v) => !v && close()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{paper ? "종이로 받은 승인 기록" : "승인 기록"}</DialogTitle>
            <DialogDescription>
              {paper
                ? "고객이 종이 변경계약서에 서명했을 때 남깁니다. 지금 문서가 굳고 지급회차 금액이 바뀝니다."
                : "고객이 링크 대신 종이·구두로 합의했을 때 남깁니다. 지급회차 금액이 바뀝니다."}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-y-2">
            <Field label="승인자 이름">
              <Input inputSize="sm" maxLength={50} name="accepted-by" value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label="메모(선택)">
              <Textarea
                className="min-h-16 text-sm"
                maxLength={500}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </Field>
          </div>
          <DialogFooter>
            <Button disabled={pending} size="sm" variant="ghost" onClick={close}>
              취소
            </Button>
            <Button
              disabled={pending || name.trim() === ""}
              size="sm"
              variant="primary"
              onClick={() =>
                void run(
                  "승인을 기록하지 못했습니다",
                  () => acceptChangeOrderByStaff(changeOrderId, { name, note }),
                  () => {
                    toast.success("승인을 기록했습니다 — 지급회차 금액이 바뀌었습니다");
                    close();
                  }
                )
              }>
              승인 기록
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open === "reject" || open === "void"} onOpenChange={(v) => !v && close()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{open === "reject" ? "거절 기록" : "변경공사 파기"}</DialogTitle>
            <DialogDescription>
              {open === "reject"
                ? "고객이 변경 내용을 받아들이지 않았을 때 남깁니다. 금액은 바뀌지 않습니다."
                : "이 변경공사를 없던 것으로 합니다. 금액은 바뀌지 않고, 되돌릴 수 없습니다."}
            </DialogDescription>
          </DialogHeader>
          <Field label="사유(선택)">
            <Textarea
              className="min-h-16 text-sm"
              maxLength={1000}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
          <DialogFooter>
            <Button disabled={pending} size="sm" variant="ghost" onClick={close}>
              취소
            </Button>
            <Button
              disabled={pending}
              size="sm"
              variant="destructive"
              onClick={() => {
                const reject = open === "reject";
                void run(
                  reject ? "거절을 기록하지 못했습니다" : "파기하지 못했습니다",
                  () => (reject ? rejectChangeOrder(changeOrderId, reason) : voidChangeOrder(changeOrderId, reason)),
                  () => {
                    toast.success(reject ? "거절을 기록했습니다" : "파기했습니다");
                    close();
                  }
                );
              }}>
              {open === "reject" ? "거절 기록" : "파기"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChangeOrderActions;
